import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errorUtils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Edit, Trash2, Lock, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = ({ user, onLogout, theme, onToggleTheme }) => {
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null); // {id, email} of user whose pwd we're resetting
  const [pwdValue, setPwdValue] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // full user object being edited
  const [editCampaigns, setEditCampaigns] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const { t } = useLanguage();
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'operatore',
    campaigns: [],
  });

  useEffect(() => {
    fetchUsers();
    fetchCampaigns();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error(t('users_required_fields'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/register`, newUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('users_created_ok'));
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        role: 'operatore',
        campaigns: [],
      });
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, t('users_create_error')));
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${userId}`,
        { is_active: !isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(!isActive ? t('users_activated_ok') : t('users_deactivated_ok'));
      fetchUsers();
    } catch (error) {
      toast.error(t('users_update_error'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('users_delete_confirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('users_deleted_ok'));
      fetchUsers();
    } catch (error) {
      toast.error(t('users_delete_error'));
    }
  };

  const handleCampaignToggle = (campaignId) => {
    setNewUser((prev) => {
      const already = prev.campaigns.includes(campaignId);
      return {
        ...prev,
        campaigns: already
          ? prev.campaigns.filter((c) => c !== campaignId)
          : [...prev.campaigns, campaignId],
      };
    });
  };

  const openEditCampaigns = (userObj) => {
    setEditTarget(userObj);
    setEditCampaigns(userObj.campaigns || []);
  };

  const toggleEditCampaign = (campaignId) => {
    setEditCampaigns((prev) =>
      prev.includes(campaignId) ? prev.filter((c) => c !== campaignId) : [...prev, campaignId]
    );
  };

  const handleSaveCampaigns = async () => {
    if (!editTarget) return;
    if (editCampaigns.length === 0) {
      toast.error(t('users_edit_campaigns_min') || 'Seleziona almeno una campagna');
      return;
    }
    setEditSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${editTarget.id}`,
        { campaigns: editCampaigns },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('users_edit_campaigns_ok') || 'Campagne aggiornate');
      setEditTarget(null);
      setEditCampaigns([]);
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, t('users_update_error')));
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetUserPassword = async () => {
    if (!pwdTarget) return;
    if (pwdValue.length < 8) {
      toast.error(t('sec_pwd_min'));
      return;
    }
    setPwdSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/users/${pwdTarget.id}/reset-password`,
        { new_password: pwdValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('admin_reset_pwd_ok'));
      setPwdTarget(null);
      setPwdValue('');
    } catch (error) {
      toast.error(extractErrorMessage(error, t('admin_reset_pwd_error')));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                data-testid="users-title"
              >
                {t('users_title')}
              </h1>
              <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                {users.length} {t('users_total')}
              </p>
            </div>
            <Button
              className="btn-primary"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-user-button"
            >
              <Plus size={20} className="mr-2" />
              {t('new_user')}
            </Button>
          </div>

          <div
            className="rounded-lg border overflow-hidden"
            style={{
              backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
              borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead
                  style={{
                    backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA',
                    borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}`,
                  }}
                >
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Nome
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Email
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Ruolo
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Campagne
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Stato
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs uppercase tracking-wider"
                      style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    >
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      style={{ borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td
                        className="px-4 py-3 font-medium"
                        style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                      >
                        {u.full_name}
                      </td>
                      <td
                        className="px-4 py-3 text-sm"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                      >
                        {u.email}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-1 rounded text-xs font-medium uppercase"
                          style={{
                            backgroundColor: u.role === 'admin' ? '#0F62FE15' : '#52525215',
                            color: u.role === 'admin' ? '#0F62FE' : '#525252',
                          }}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3 text-sm"
                        style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                        data-testid={`user-campaigns-${u.id}`}
                      >
                        {u.role === 'admin' ? (
                          t('users_all_campaigns') || 'Tutte'
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(u.campaigns || []).length === 0 && (
                              <span style={{ color: '#DA1E28' }}>—</span>
                            )}
                            {(u.campaigns || []).map((cid) => {
                              const camp = campaigns.find((c) => c.id === cid);
                              return (
                                <span
                                  key={cid}
                                  className="px-2 py-0.5 rounded text-xs"
                                  style={{ backgroundColor: '#0F62FE15', color: '#0F62FE' }}
                                >
                                  {camp ? camp.name : cid.slice(0, 6)}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active ? (
                          <span
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ backgroundColor: '#19803815', color: '#198038' }}
                          >
                            Attivo
                          </span>
                        ) : (
                          <span
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ backgroundColor: '#DA1E2815', color: '#DA1E28' }}
                          >
                            Disattivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {u.role === 'operatore' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditCampaigns(u)}
                              data-testid={`edit-campaigns-${u.id}`}
                              title={t('users_edit_campaigns_title') || 'Modifica campagne'}
                            >
                              <Edit size={16} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            data-testid={`toggle-user-${u.id}`}
                          >
                            <Lock size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setPwdTarget({ id: u.id, email: u.email }); setPwdValue(''); }}
                            data-testid={`reset-pwd-${u.id}`}
                            title={t('admin_reset_pwd')}
                          >
                            <KeyRound size={16} />
                          </Button>
                          {u.email !== 'admin@crm.com' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(u.id)}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('users_create_new_title')}</DialogTitle>
            <DialogDescription>{t('users_create_new_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user-name">{t('users_full_name')}</Label>
                <Input
                  id="user-name"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Mario Rossi"
                  className="mt-1"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="user-email">{t('users_email_required')}</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="mario.rossi@esempio.it"
                  className="mt-1"
                  data-testid="user-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user-password">{t('users_password_required')}</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label htmlFor="user-role">{t('users_role_required')}</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(val) => setNewUser({ ...newUser, role: val })}
                >
                  <SelectTrigger className="mt-1" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operatore">{t('operator_role')}</SelectItem>
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newUser.role === 'operatore' && (
              <div>
                <Label>{t('users_campaigns_label') || 'Campagne assegnate'}</Label>
                <p className="text-xs mt-1 mb-2" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                  {t('users_campaigns_hint') || 'Seleziona una o più campagne. L\'operatore vedrà solo i lead delle campagne selezionate.'}
                </p>
                <div className="mt-2 space-y-2">
                  {campaigns.filter(c => c.is_active !== false).map((c) => {
                    const selected = newUser.campaigns.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        className="flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors"
                        style={{
                          borderColor: selected ? '#0F62FE' : (theme === 'dark' ? '#262626' : '#E0E0E0'),
                          backgroundColor: selected ? '#0F62FE15' : 'transparent',
                        }}
                        onClick={() => handleCampaignToggle(c.id)}
                        data-testid={`campaign-toggle-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => handleCampaignToggle(c.id)}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="font-semibold">{c.name}</div>
                          {c.description && (
                            <div className="text-xs opacity-70">{c.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Button
              className="w-full btn-primary"
              onClick={handleCreateUser}
              data-testid="user-submit-button"
            >
              {t('users_submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: Reset user password Dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(open) => { if (!open) { setPwdTarget(null); setPwdValue(''); } }}>
        <DialogContent className="max-w-md" data-testid="reset-user-pwd-dialog">
          <DialogHeader>
            <DialogTitle>
              {(t('admin_reset_pwd_title') || 'Password for $email').replace('$email', pwdTarget?.email || '')}
            </DialogTitle>
            <DialogDescription>{t('admin_reset_pwd_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>{t('sec_new_password')}</Label>
              <Input
                type="text"
                value={pwdValue}
                onChange={(e) => setPwdValue(e.target.value)}
                className="mt-1 font-mono"
                placeholder={t('admin_reset_pwd_placeholder') || 'New password'}
                data-testid="reset-user-pwd-input"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setPwdTarget(null); setPwdValue(''); }}>
                {t('cancel') || 'Annulla'}
              </Button>
              <Button
                className="btn-primary"
                disabled={pwdSaving || pwdValue.length < 8}
                onClick={handleResetUserPassword}
                data-testid="reset-user-pwd-submit"
              >
                <KeyRound size={14} className="mr-2" />
                {pwdSaving ? t('sec_pwd_saving') : t('sec_pwd_save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Campaigns Dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => { if (!open) { setEditTarget(null); setEditCampaigns([]); } }}
      >
        <DialogContent className="max-w-lg" data-testid="edit-campaigns-dialog">
          <DialogHeader>
            <DialogTitle>
              {(t('users_edit_campaigns_title') || 'Modifica campagne') + ' — ' + (editTarget?.full_name || '')}
            </DialogTitle>
            <DialogDescription>
              {t('users_edit_campaigns_desc') || 'Seleziona una o più campagne. L\'operatore vedrà solo i lead delle campagne selezionate.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {campaigns.filter((c) => c.is_active !== false).map((c) => {
              const selected = editCampaigns.includes(c.id);
              return (
                <div
                  key={c.id}
                  className="flex items-start gap-2 p-3 rounded border cursor-pointer transition-colors"
                  style={{
                    borderColor: selected ? '#0F62FE' : (theme === 'dark' ? '#262626' : '#E0E0E0'),
                    backgroundColor: selected ? '#0F62FE15' : 'transparent',
                  }}
                  onClick={() => toggleEditCampaign(c.id)}
                  data-testid={`edit-campaign-toggle-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Checkbox
                    checked={selected}
                    onCheckedChange={() => toggleEditCampaign(c.id)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    {c.description && (
                      <div className="text-xs opacity-70">{c.description}</div>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end gap-2 pt-3">
              <Button
                variant="outline"
                onClick={() => { setEditTarget(null); setEditCampaigns([]); }}
                data-testid="edit-campaigns-cancel"
              >
                {t('cancel') || 'Annulla'}
              </Button>
              <Button
                className="btn-primary"
                disabled={editSaving || editCampaigns.length === 0}
                onClick={handleSaveCampaigns}
                data-testid="edit-campaigns-save"
              >
                {editSaving ? (t('users_edit_saving') || 'Salvataggio...') : (t('users_edit_save') || 'Salva')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
